import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { ImageUpload } from '@/components/config-editor/form-elements/ImageUpload';
import { useEffectiveLogoUrl } from '@/hooks/use-effective-logo-url';

/**
 * Store logo input component
 * Allows users to upload a custom logo for their store
 */
export const StoreLogoInput = () => {
  const { t } = useTranslation('stores');
  const defaultLogoUrl = useEffectiveLogoUrl();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<{ logoUrl: string }>();

  const logoUrl = watch('logoUrl');
  const hasCustomLogo = logoUrl && logoUrl.length > 0;

  return (
    <div className="space-y-2">
      <Label>{t('logo.label')}</Label>

      {/* Show placeholder notice when no custom logo is set */}
      {!hasCustomLogo && (
        <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          {defaultLogoUrl && (
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-amber-200 bg-white p-1 opacity-60">
              <img src={defaultLogoUrl} alt={t('logo.placeholderAlt')} className="h-full w-full object-contain" />
            </div>
          )}
          <div className="flex-1">
            <p className="flex items-center gap-1.5 text-sm font-medium text-amber-800">
              <AlertCircle className="h-3.5 w-3.5" />
              {t('logo.emptyTitle')}
            </p>
            <p className="text-xs text-amber-700">
              {t('logo.emptyDescription')}
            </p>
          </div>
        </div>
      )}

      <ImageUpload url={logoUrl} onImageAvailable={(url) => setValue('logoUrl', url)} />
      {errors.logoUrl && <p className="text-sm text-red-500">{errors.logoUrl.message}</p>}
      <p className="text-muted-foreground text-xs">
        {t('logo.helpText')}
      </p>
    </div>
  );
};
